import React from 'react';
import * as PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  Button, Form, Icon, Input, Layout, PageHeader, Popover, Row, Spin, Tag, Typography,
} from 'antd';
import '../../../../components/panels/Panels.css';
import { FormattedMessage, injectIntl } from 'react-intl';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';

const { Text } = Typography;

/**
 * Popup to amend the title/description
 */
const DescriptionPopup = injectIntl(({
  form, descData, descValidator, descMaxLength, saveFunction, cancelFunction, buttonIdSave, buttonIdCancel, isLoading, validationBox,
}) => (
  <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); saveFunction(form, descData); }}>
    <ViaValidationAlert validationError={validationBox} />
    <DescriptionField form={form} descValue={descData} maxLength={descMaxLength} checkForDuplicates={descValidator} />
    <Row type="flex" justify="end" style={{ marginTop: '-0.5em' }}>
      <Button loading={isLoading} size="small" style={{ marginRight: '0.5em' }} onClick={() => cancelFunction(form)}><FormattedMessage id={buttonIdCancel} /></Button>
      <Button loading={isLoading} size="small" type="primary" htmlType="submit"><FormattedMessage id={buttonIdSave} /></Button>
    </Row>
  </Form>
));

/**
 * The field for the description label.  Allows clear, and max length set.  Required plus extra duplicate validation.
 * The rules are injected into the form using the form field decorator.
 */
const DescriptionField = injectIntl(({
  form, descValue, intl, maxLength, checkForDuplicates,
}) => (
  <Form.Item required label={<FormattedMessage id="description" />}>
    { form.getFieldDecorator('description', {
      initialValue: descValue || '',
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        {
          validator(rule, value, callback) {
            if (value !== descValue && checkForDuplicates(value)) {
              callback(intl.formatMessage({ id: 'Duplicate' }));
            }
            callback();
          },
        },
      ],
    })(
      <Input
        type="text"
        maxLength={maxLength}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * Bynx Configuration Back Page (a panel with a title, back button, and padding around the contents
 *                               with options for published status and an edit button
 * (popoverActive with all other popoverXXX attributes OR set showEdit false OR set editFunctionNoPopover)
 * @returns {*}
 * @constructor
 */
function ConfigBackHeading({
  form, titleValue, active, actions, children, onBack,
  popoverActive, popoverTitleId, popoverCancelIcon, popoverDescData, popoverDescValidator,
  popoverCancelTextId, popoverCancelFunction, popoverSaveTextId, popoverSaveFunction, popoverLoading, popoverValidationBox,
  popoverVisible, popoverSetVisibleFunction, showEdit, editFunctionNoPopover, isLoading,
}) {
  return (
    <Layout className="via-panel-layout">
      <Row align="top" justify="center" type="flex">
        <PageHeader
          style={{ width: '100%' }}
          onBack={onBack}
          title={(
            <Row type="flex" justify="space-between" align="middle">
              {
              isLoading
                ? <Spin />
                : (
                  <div>
                    <Text style={{ marginRight: '0.7em' }}>{titleValue}</Text>
                    <Popover
                      style={{ display: popoverActive ? '' : 'none' }}
                      placement="bottom"
                      title={(
                        <Row type="flex" justify="space-between" align="middle">
                          <FormattedMessage id={popoverTitleId} />
                          {
                            popoverCancelIcon
                              ? <Icon type="close" onClick={() => popoverCancelFunction(form)} />
                              : <></>
                          }
                        </Row>
                      )}
                      content={(
                        <DescriptionPopup
                          form={form}
                          descData={popoverDescData}
                          descValidator={popoverDescValidator}
                          descMaxLength={250}
                          saveFunction={aForm => popoverSaveFunction(aForm)}
                          buttonIdSave={popoverSaveTextId}
                          cancelFunction={aForm => popoverCancelFunction(aForm)}
                          buttonIdCancel={popoverCancelTextId}
                          isLoading={popoverLoading}
                          validationBox={popoverValidationBox}
                        />
                      )}
                      trigger={popoverActive ? 'click' : 'null'}
                      overlayStyle={{ width: '350px' }}
                      visible={popoverVisible}
                      onVisibleChange={(vis) => {
                        if (!vis) {
                          popoverCancelFunction(form);
                        }
                      }}
                    >
                      <Button style={{ display: showEdit ? '' : 'none' }} key={Math.random()} loading={isLoading} type="primary" size="small" icon="edit" onClick={() => { popoverSetVisibleFunction(true); editFunctionNoPopover(); }}><FormattedMessage id="edit" /></Button>
                    </Popover>
                  </div>
                )
              }
            </Row>
          )}
          extra={(
            isLoading
              ? <Spin />
              : (
                <Row key={Math.random()} type="flex" justify="end" align="middle">
                  <Tag
                    color={active ? 'blue' : 'volcano'}
                    style={{
                      display: active == null ? 'none' : '',
                      lineHeight: '22px',
                      height: '22px',
                      marginTop: '0.15em',
                    }}
                  >
                    <FormattedMessage id={active ? 'published' : 'notPublished'} />
                  </Tag>
                  {actions}
                </Row>
              )
          )
          }
        />
      </Row>
      <Row>
        {children}
      </Row>
    </Layout>
  );
}

// Validate the prop type parameters.
ConfigBackHeading.propTypes = {
  form: PropTypes.oneOfType([PropTypes.object]).isRequired,
  titleValue: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  active: PropTypes.bool,
  popoverActive: PropTypes.bool,
  popoverTitleId: PropTypes.string,
  popoverCancelIcon: PropTypes.bool,
  popoverDescData: PropTypes.string,
  popoverDescValidator: PropTypes.func,
  popoverCancelTextId: PropTypes.string,
  popoverCancelFunction: PropTypes.func,
  popoverSaveTextId: PropTypes.string,
  popoverSaveFunction: PropTypes.func,
  popoverLoading: PropTypes.bool,
  popoverValidationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  popoverVisible: PropTypes.bool,
  popoverSetVisibleFunction: PropTypes.func,
  showEdit: PropTypes.bool,
  editFunctionNoPopover: PropTypes.func,
  isLoading: PropTypes.bool,
  actions: PropTypes.oneOfType([PropTypes.element,
    PropTypes.arrayOf(PropTypes.element)]),
  children: PropTypes.oneOfType([PropTypes.element,
    PropTypes.arrayOf(PropTypes.element)]).isRequired,
};

ConfigBackHeading.defaultProps = {
  active: null,
  popoverActive: false,
  popoverTitleId: '',
  popoverCancelIcon: true,
  popoverDescData: '',
  popoverDescValidator: () => {},
  popoverCancelTextId: '',
  popoverCancelFunction: () => {},
  popoverSaveTextId: '',
  popoverSaveFunction: () => {},
  popoverLoading: false,
  popoverValidationBox: null,
  popoverVisible: false,
  popoverSetVisibleFunction: () => {},
  showEdit: false,
  editFunctionNoPopover: () => {},
  isLoading: false,
  actions: null,
};

export default withRouter(Form.create({ name: 'edit-config-desc' })(ConfigBackHeading));
